import { forwardRef, useEffect, useRef, type InputHTMLAttributes } from 'react';
import { Check, Minus } from 'lucide-react';
import { cn } from '@/lib/utils';

export interface CheckboxProps extends InputHTMLAttributes<HTMLInputElement> {
  /** Libellé rendu à droite, cliquable (le tout est enveloppé dans un <label>). */
  label?: string;
  /** État indéterminé (icône Minus) — positionné sur l'élément natif via ref. */
  indeterminate?: boolean;
}

/**
 * Checkbox stylée tokens : l'input natif reste dans le flux (sr-only, PAS
 * display:none) pour rester focusable au clavier ; la boîte visuelle est
 * pilotée en CSS via peer-checked / peer-indeterminate / peer-focus-visible.
 */
export const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(
  ({ className, label, indeterminate = false, disabled, ...props }, ref) => {
    const innerRef = useRef<HTMLInputElement | null>(null);

    useEffect(() => {
      if (innerRef.current) innerRef.current.indeterminate = indeterminate;
    }, [indeterminate]);

    return (
      <label
        className={cn(
          'inline-flex items-center gap-2 select-none',
          disabled ? 'cursor-not-allowed opacity-40' : 'cursor-pointer',
          className,
        )}
      >
        <input
          ref={(node) => {
            innerRef.current = node;
            if (typeof ref === 'function') ref(node);
            else if (ref) ref.current = node;
          }}
          type="checkbox"
          disabled={disabled}
          className="peer sr-only"
          {...props}
        />
        <span
          aria-hidden="true"
          className={cn(
            'flex h-4 w-4 shrink-0 items-center justify-center rounded border border-border bg-surface transition-colors duration-150',
            'peer-checked:border-accent peer-checked:bg-accent',
            'peer-indeterminate:border-accent peer-indeterminate:bg-accent',
            'peer-focus-visible:ring-2 peer-focus-visible:ring-accent peer-focus-visible:ring-offset-2 peer-focus-visible:ring-offset-surface',
            '[&>svg]:opacity-0 peer-checked:[&>svg]:opacity-100 peer-indeterminate:[&>svg]:opacity-100',
          )}
        >
          {indeterminate ? (
            <Minus className="h-3 w-3 text-white" strokeWidth={3} />
          ) : (
            <Check className="h-3 w-3 text-white" strokeWidth={3} />
          )}
        </span>
        {label && <span className="text-[13px] text-text">{label}</span>}
      </label>
    );
  },
);

Checkbox.displayName = 'Checkbox';
